import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { FILE_TYPES } from '@app/constants';
import { SystemMessageService } from '@app/core/services/system-message.service';
import { SelectedFile, MediaFormat } from '@app/models/media-files';
import { VideoThumbnailService } from '@shared/services/video-thumbnail.service';

interface FilePrview {
  index: number;
  selectedFile: File;
  fileSize: number;
  filePreview: string;
  format: MediaFormat;
}

@Component({
  selector: 'flow-drag-to-upload',
  templateUrl: './drag-to-upload.component.html',
  styleUrls: ['./drag-to-upload.component.scss'],
})
export class DragToUploadComponent implements OnChanges {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Input() title = 'createMedia.selectFromLibrary';
  @Input() reset = false;
  @Input() multiple = false;
  @Input() supportedFileTypes: string[] = [];
  @Input() maxUploadLimit = 1;
  @Input() maxFileSize = 1024 * 1024 * 1024; // 1GB;
  @Input() disabled = false;
  @Input() filePreview: string | null = null;
  @Output() maxUploadLimitError = new EventEmitter<boolean>();
  @Output() selectedFile = new EventEmitter<FilePrview>();
  @Output() uploadingLoader = new EventEmitter<boolean>();

  supportedFileTypeLabel = '';
  selectedBody: SelectedFile;
  isDragging = false;
  isLoading = false;
  isVideoSupported = false;
  inputAccept = '';
  MediaFormat = MediaFormat;
  invalidToken = false;

  constructor(
    private systemMessageService: SystemMessageService,
    private translateService: TranslateService,
    private videoThumbnailService: VideoThumbnailService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reset'] && changes['reset'].currentValue) {
      this.selectedBody = null;
      this.fileInput.nativeElement.value = '';
    }

    if (changes['filePreview'] && changes['filePreview'].currentValue) {
      this.selectedBody = {
        selectedFile: new File([''], 'existing_image'),
        filePreview: this.filePreview,
        format: MediaFormat.Image,
        fileSize: 0,
        index: '0'
      };
    }

    this.supportedFileTypeLabel = this.checkSupportedFileTypes(this.supportedFileTypes);
  }

  checkMaxUploadLimit(fileCount?: number): boolean {
    if (fileCount && this.maxUploadLimit - fileCount < 0) {
      this.maxUploadLimitError.emit(true);
      return true;
    }

    if (this.maxUploadLimit === 0) {
      this.maxUploadLimitError.emit(true);
      return true;
    }

    return false;
  }

  checkSupportedFileTypes(supportedFileTypes: string[]) {
    const hasImages = supportedFileTypes.some(type => FILE_TYPES.image.includes(type));
    const hasVideos = supportedFileTypes.some(type => FILE_TYPES.video.includes(type));
    const hasJson = supportedFileTypes.some(type => FILE_TYPES.json.includes(type));

    let result: string[] = [];
    const input = [];

    if (hasImages) {
      result.push('PNG', 'JPG', 'JPEG');
      input.push('image/*');
    }

    if (hasVideos) {
      result.push('MP4');
      input.push('video/*');
      this.isVideoSupported = true;
    }

    if (hasJson) {
      result.push('JSON');
      input.push('application/json');
    }

    this.inputAccept = input.join(', ');

    return result.join(', ');
  }

  triggerFileInput() {
    if (this.selectedBody) {
      return;
    }
    this.fileInput.nativeElement.click();
  }

  selectFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.proccessSelectedFiles(input.files);
  }

  proccessSelectedFiles(selectedFiles: FileList): void {
    const uploadedFiles = [];
    const maxUploadLimit = this.checkMaxUploadLimit(selectedFiles.length);

    if (maxUploadLimit) {
      if (this.multiple) {
        this.systemMessageService.error(
          this.translateService.instant('createMedia.error.maxUploadLimit', { numberOfFiles: this.maxUploadLimit })
        );
      } else {
        this.systemMessageService.error(this.translateService.instant('createMedia.error.singleUpload'));
      }
      return;
    }

    if (!selectedFiles) {
      console.warn('No file selected');
      return;
    }

    this.isLoading = true;
    this.uploadingLoader.emit(this.isLoading);

    Array.from(selectedFiles).forEach(file => {
      uploadedFiles.push(this.readyFile(file));
    });

    Promise.all(uploadedFiles)
      .then((files: any) => {
        files.forEach(file => {
          this.selectedBody = this.multiple ? null : file;
          this.selectedFile.emit(file);
        });
      })
      .catch(err => {
        console.error('An error occurred during file reading:', err);
      })
      .finally(() => {
        this.isLoading = false;
        this.uploadingLoader.emit(this.isLoading);
      });
  }

  readyFile(selectedFile): Promise<any> | void {
    if (!this.isValidFileType(selectedFile?.type)) {
      this.systemMessageService.error(this.translateService.instant('createMedia.error.supports'));
      this.isLoading = false;
      this.uploadingLoader.emit(this.isLoading);
      return;
    }

    // Only generate preview for files smaller than 1GB.
    // For performance reasons skip preview for large files
    if (selectedFile.size > this.maxFileSize) {
      const maxSizeInMB = this.maxFileSize / (1024 * 1024);
      this.systemMessageService.error(`The file with name ${selectedFile.name} is to big. Max file size is ${maxSizeInMB}MB `);
      this.isLoading = false;
      this.uploadingLoader.emit(this.isLoading);
      return;
    }

    return new Promise((resolve, reject) => {
      const file = {
        index: null,
        selectedFile,
        fileSize: selectedFile.size,
        filePreview: null,
        format: this.getFileFormat(selectedFile),
      };

      const reader = new FileReader();

      reader.onload = async (e: ProgressEvent<FileReader>) => {
        if (file?.format === MediaFormat.Video) {
          try {
            const duration = await this.getVideoDuration(file?.selectedFile);
            file.selectedFile.videoDuration = duration.toFixed(2);

            const thumbnail = await this.videoThumbnailService.generateThumbnail(file.selectedFile);
            file.filePreview = thumbnail;
          } catch (error) {
            file.selectedFile.videoDuration = 0;
            this.systemMessageService.error(this.translateService.instant('createMedia.error.thumbnailGeneration'));
          }
        }
        if (file?.format === MediaFormat.Image || file?.format === MediaFormat.JSON) {
          file.filePreview = e.target?.result as string;
        }

        resolve(file);
      };

      reader.onerror = err => {
        console.error('File reading failed');
        reject(err);
      };

      if (file?.format === MediaFormat.JSON) {
        reader.readAsText(selectedFile);
      } else {
        reader.readAsDataURL(selectedFile);
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    this.proccessSelectedFiles(event.dataTransfer?.files);
  }

  isValidFileType(fileType: string): boolean {
    const allowedTypes = this.supportedFileTypes;
    return allowedTypes.includes(fileType);
  }

  getFileFormat(file: File): MediaFormat {
    if (file.type.startsWith('image/')) {
      return MediaFormat.Image;
    }

    if (file.type.includes('json')) {
      return MediaFormat.JSON;
    }

    return MediaFormat.Video;
  }

  getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.src = window.URL.createObjectURL(file);
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = error => {
        reject(error);
      };
    });
  }

  resetSelectedFile(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.selectedBody = null;
    this.fileInput.nativeElement.value = '';
    this.selectedFile.emit(null);
  }
}
